// pages/Preparation.jsx
// Lists orders in the "Preparation" stage of the ORDER workflow (serviceType === 'order').
// Advance action moves an order into the next stage ("Processing").
import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSeller } from '../contexts/SellerContext';
import PageHeader from '../components/PageHeader';
import Button from '../ui/components/Button';
import { useNavigate } from 'react-router-dom';
import './OrderStatus.css';

const STATUS = 'Preparation';
const NEXT_STATUS = 'Processing';

const toInputDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatIndianDate = (val) => {
  let d = val;
  if (!d) return '—';
  if (typeof d.toDate === 'function') d = d.toDate();
  else if (typeof d === 'object' && typeof d.seconds === 'number') d = new Date(d.seconds * 1000);
  else d = new Date(d);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const getTodayInput = () => toInputDate(new Date());

// Latest selectable completion date: today + orderServeSla (days)
const getMaxServeDate = (orderServeSla) => {
  const days = parseInt(orderServeSla, 10);
  const offset = Number.isFinite(days) && days > 0 ? days : 0;
  const max = new Date();
  max.setDate(max.getDate() + offset);
  return toInputDate(max);
};

const Preparation = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [modalOrder, setModalOrder] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchOrders = useCallback(async () => {
    if (!sellerId) return;

    try {
      setLoading(true);
      setError(null);
      const q = query(
        collection(db, 'orderList'),
        where('sellerId', '==', sellerId),
        where('serviceType', '==', 'order'),
        where('orderStatus', '==', STATUS)
      );
      const snapshot = await getDocs(q);

      const fetchedOrders = [];
      snapshot.forEach((docSnap) => {
        fetchedOrders.push({
          firestoreId: docSnap.id,
          ...docSnap.data(),
        });
      });
      console.log(fetchedOrders)
      setOrders(fetchedOrders);
    } catch (err) {
      console.error('Error fetching preparation orders:', err);
      setError('Failed to load preparation orders.');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openProcessingModal = useCallback((order) => {
    if (order.buyerConfirmation !== true) {
      alert('Buyer confirmation is required before starting processing.');
      return;
    }
    const maxDate = getMaxServeDate(order.orderServeSla - (order.orderServeSla * 20) / 100);
    setSelectedDate(maxDate);
    setModalOrder(order);
  }, []);

  const closeProcessingModal = useCallback(() => {
    if (pendingId) return;
    setModalOrder(null);
    setSelectedDate('');
  }, [pendingId]);

  const confirmProcessing = useCallback(async () => {
    if (!modalOrder || pendingId) return;

    const today = getTodayInput();
    const maxDate = getMaxServeDate(modalOrder.orderServeSla);
    if (!selectedDate) {
      alert('Please select a completion date.');
      return;
    }
    if (selectedDate < today || selectedDate > maxDate) {
      alert(`Completion date must be between today and ${maxDate} (your SLA).`);
      return;
    }

    setPendingId(modalOrder.firestoreId);

    try {
      const orderRef = doc(db, 'orderList', modalOrder.firestoreId);
      await updateDoc(orderRef, {
        orderStatus: NEXT_STATUS,
        completeOrderDate: selectedDate,
        updatedAt: new Date(),
      });
      setOrders((prev) => prev.filter((o) => o.firestoreId !== modalOrder.firestoreId));
      setModalOrder(null);
      setSelectedDate('');
    } catch (err) {
      console.error('Error updating order:', err);
      alert(`Failed to update order: ${err.message}`);
    } finally {
      setPendingId(null);
    }
  }, [modalOrder, pendingId, selectedDate]);

  if (loading) {
    return (
      <>
        <PageHeader title="Preparation Orders" />
        <div className="order-status-container">Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Preparation Orders" />
        <div className="order-status-container">
          <p className="error">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Preparation Orders" />

      <div className="order-status-container">
        <p className="results-count">
          {orders.length} order{orders.length !== 1 ? 's' : ''} in preparation
        </p>

        <div className="orders-grid">
          {orders.length === 0 ? (
            <p className="no-results">No orders in preparation.</p>
          ) : (
            orders.map((order) => {
              const imageUrl = order.images?.[0]?.url?.trim();
              const variantSummary = order.selectedVariants?.length > 0
                ? order.selectedVariants.map((v) => `${v.size} x ${v.quantity}`).join(', ')
                : null;

              return (
                <div key={order.firestoreId} className="order-card">
                  <div
                    className="order-image"
                    onClick={() => navigate(`/product/${order.id}`, { state: { product: order } })}
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt={order.productName} />
                    ) : (
                      <div className="image-placeholder">No Image</div>
                    )}
                  </div>

                  <div className="order-info">
                    <div className="order-header">
                      <h5>{order.category}/{order.productName}</h5>
                      <span className={`status-badge status-${order.orderStatus?.toLowerCase()}`}>
                        {order.orderStatus}
                      </span>
                    </div>
                    <p className="order-spec">
                      {order.specification}/{order.styleType}
                    </p>
                    {variantSummary && (
                      <p className="spec-label">{variantSummary}</p>
                    )}
                    {order.createdAt && (
                      <p className="order-date">
                        {formatIndianDate(order.createdAt)}
                      </p>
                    )}

                    <div className="action-btn">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openProcessingModal(order)}
                        disabled={pendingId !== null || order.buyerConfirmation !== true}
                        title={
                          order.buyerConfirmation === true
                            ? `Start ${NEXT_STATUS}`
                            : 'Locked until buyer confirms the order'
                        }
                      >
                        {order.buyerConfirmation === true
                          ? pendingId === order.firestoreId
                            ? 'Moving...'
                            : `Start ${NEXT_STATUS}`
                          : 'Awaiting Buyer'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {modalOrder && (
        <div className="modal-overlay" onClick={closeProcessingModal} role="dialog" aria-modal="true">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Set Completion Date</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={closeProcessingModal}
                disabled={pendingId !== null}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-hint">
                Select the expected completion date for <strong>{modalOrder.productName}</strong>.
                {modalOrder.orderServeSla
                  ? ` It must be on or before ${getMaxServeDate(modalOrder.orderServeSla)} (today + ${modalOrder.orderServeSla} day(s) SLA).`
                  : ''}
              </p>

              <div className="form-group">
                <label htmlFor="completeOrderDate">Completion Date</label>
                <input
                  id="completeOrderDate"
                  type="date"
                  className="form-input"
                  value={selectedDate}
                  min={getTodayInput()}
                  max={getMaxServeDate(modalOrder.orderServeSla - (modalOrder.orderServeSla * 20) / 100)}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-footer">
              <Button
                variant="secondary"
                size="sm"
                onClick={closeProcessingModal}
                disabled={pendingId !== null}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={confirmProcessing}
                disabled={pendingId !== null || !selectedDate}
              >
                {pendingId === modalOrder.firestoreId ? 'Saving...' : `Start ${NEXT_STATUS}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Preparation;
